import type { VoidComponent } from "solid-js";
import { For, Match, Show, Switch } from "solid-js";
import type { IconName } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import { isNil } from "lodash-es";
import type { PolymorphicProps } from "@ark-ui/solid";
import { Menu } from "@ark-ui/solid";
import { Portal } from "solid-js/web";
import Text from "../text/Text";
import DynamicValueListItem from "../menu/listItems/DynamicValueListItem";
import type { SelectOption } from "../../types/Menu";
import AvatarListItem from "../menu/listItems/AvatarListItem";
import { narrow } from "../../utils/solidUtils";
import ImageListItem from "../menu/listItems/ImageListItem";
import IconListItem from "../menu/listItems/IconListItem";
import TextListItem from "../menu/listItems/TextListItem";

type GenericInteractivePillProps = {
  icon?: IconName;
  label: string;
};

type InteractivePillProps = {
  actionIcon: "x" | "chevronDown";
} & GenericInteractivePillProps;

const InteractivePill: VoidComponent<InteractivePillProps> = (props) => (
  <span class="flex max-w-43.5 items-center gap-0.5 rounded-sm bg-surface-subtle px-1 text-small-medium text-status-neutral hover:bg-surface-subtle-hover">
    <Show when={!isNil(props.icon)}>
      <Icon name={props.icon!} type="solid" size="xs" class="inline shrink-0 text-status-neutral" />
    </Show>
    <Text text={props.label} truncate />
    <Icon name={props.actionIcon} type="solid" size="xs" class="inline shrink-0 text-primary" />
  </span>
);

type MenuPillProps<T> = {
  onSelect: (key: T) => void;
  items: SelectOption<T>[];
  Trigger?: PolymorphicProps<"button">["asChild"];
} & GenericInteractivePillProps;

export const MenuPill = <T,>(props: MenuPillProps<T>) => (
  <Menu.Root
    lazyMount
    unmountOnExit
    positioning={{
      placement: "bottom-end",
      gutter: 4,
    }}
  >
    <Show
      when={props.Trigger}
      fallback={
        <Menu.Trigger class="group pointer-events-auto rounded-sm focus-visible:outline-default">
          <InteractivePill actionIcon="chevronDown" {...props} />
        </Menu.Trigger>
      }
    >
      <Menu.Trigger asChild={props.Trigger} />
    </Show>
    <Portal>
      <Menu.Positioner>
        <Menu.Content class="z-10 max-h-86 min-h-12 max-w-100 min-w-66 overflow-y-auto rounded-sm bg-surface-default p-1 shadow-elevation-light-3 outline-none">
          <For each={props.items}>
            {(option) => (
              <Menu.Item
                value={JSON.stringify(option.value)}
                onSelect={() => props.onSelect(option.value)}
                asChild={(arkProps) => (
                  <Switch>
                    <Match when={narrow(option, (o) => o.variant === "avatar")}>
                      {(o) => <AvatarListItem htmlProps={arkProps()} {...o()} />}
                    </Match>
                    <Match when={narrow(option, (o) => o.variant === "image")}>
                      {(o) => <ImageListItem htmlProps={arkProps()} {...o()} />}
                    </Match>
                    <Match when={narrow(option, (o) => o.variant === "icon")}>
                      {(o) => <IconListItem htmlProps={arkProps()} {...o()} />}
                    </Match>
                    <Match when={narrow(option, (o) => o.variant === "text")}>
                      {(o) => <TextListItem htmlProps={arkProps()} {...o()} />}
                    </Match>
                    <Match when={narrow(option, (o) => o.variant === "dynamicValue")}>
                      {(o) => <DynamicValueListItem htmlProps={arkProps()} {...o()} />}
                    </Match>
                  </Switch>
                )}
              />
            )}
          </For>
        </Menu.Content>
      </Menu.Positioner>
    </Portal>
  </Menu.Root>
);
